// extractions/saga.js
import { put, all, takeLatest, select, takeEvery, take, cancel, fork, cancelled } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as extDataGroupActionTypes from "../extDataGroups/actionTypes";
import * as extDataCategoryActionTypes from "../extDataCategories/actionTypes";
import * as extExclusionActionTypes from "../extExclusions/actionTypes";
import * as extVersionActionTypes from "../extVersions/actionTypes";
import * as extCustomColumnsActionTypes from "../extCustomColumns/actionTypes";

import API from "../../commonUtils/API.js";
import { handleApiError } from "../../commonUtils/Functions";
import { toast } from "react-toastify";
import constants from "../../commonUtils/constants";
import { spacesToUnderscores } from "../../commonUtils/string.helper";
import { getCookie, deleteCookie } from "../../commonUtils/cookie.helper.js";

const EMPTY_TEMP_SELECTIONS = {
  selected_columns: [],
  custom_columns: []
  };

const getDefaultColumnSelections = (pageData = {}) =>
  pageData?.defaultColumnSelections || pageData?.defaultColumnSelctions || null;

const hasAnySelections = (selections) =>
  (Array.isArray(selections?.selected_columns) && selections.selected_columns.length > 0) ||
  (Array.isArray(selections?.custom_columns) && selections.custom_columns.length > 0);

function* getExtPageDataSaga() {
  try {
      yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
      const { data } = yield API.get("/extractions/load-page-data");
      if (data.success) {
        const defaultColumnSelections = getDefaultColumnSelections(data?.data);
        yield put({ type: actionTypes.LOAD_EXT_PAGE_DATA_SUCCESS, payload: data.data });

        const extractionData = yield select((state) => state.Extractions.extractionData);
        const hasTempSelections = extractionData && (
          (Array.isArray(extractionData.temp_selected_columns) && extractionData.temp_selected_columns.length > 0) ||
          (Array.isArray(extractionData.temp_custom_columns) && extractionData.temp_custom_columns.length > 0)
        );
        const hasDefaultSelections = hasAnySelections(defaultColumnSelections);

        if (hasTempSelections) {
          yield put({
            type: extVersionActionTypes.SET_EXT_VERSION,
            payload: {
              selected_columns: extractionData.temp_selected_columns || [],
              custom_columns: extractionData.temp_custom_columns || [],
            }
          });
        } else if (hasDefaultSelections) {
          yield put({
            type: extVersionActionTypes.SET_EXT_VERSION,
            payload: { ...defaultColumnSelections }
          });
        } else {
          yield put({ type: extVersionActionTypes.RESET_EXT_VERSION });
        }
      } else {
        toast.error("Could not load page data.");
        yield put({ type: actionTypes.LOAD_EXT_PAGE_DATA_FAILURE, payload: data });
      }
  } catch (error) {
      handleApiError(error, "Oops! Something went unexpectedly wrong.");
      const data = error?.response?.data;
      yield put({ type: actionTypes.LOAD_EXT_PAGE_DATA_FAILURE, payload: data || { message: "Internal server error" } });
  }
};

function* getExtMetaDataSaga({ params }) {
  try {
    const { masterProject, projectName } = params;
    yield put({ type: actionTypes.GET_EXT_META_DATA_LOADING, payload: true });
    const apiUrl = `/parquet/meta-data?masterProject=${encodeURIComponent(masterProject)}&projectName=${encodeURIComponent(projectName)}`;
    const { data } = yield API.get(apiUrl);
    if (data.success) {
      yield put({ type: actionTypes.GET_EXT_META_DATA_SUCCESS, payload: data.data });
    } else {
      console.error('API returned success:false', data);
      toast.error("Could not load meta data.");
      yield put({ type: actionTypes.GET_EXT_META_DATA_FAILURE, payload: data });
    }
  } catch (error) {
      console.error('Error in getExtMetaDataSaga:', error);
      handleApiError(error, "Oops! Something went unexpectedly wrong.");
      const data = error?.response?.data;
      yield put({ type: actionTypes.GET_EXT_META_DATA_FAILURE, payload: data || { message: "Internal server error" } });
  } finally {
      yield put({ type: actionTypes.GET_EXT_META_DATA_LOADING, payload: false });
  }
};

function* getExtPincodeFromMasterSaga({ params }) {
  try {
    const { pincode } = params;
    yield put({ type: actionTypes.GET_EXT_PINCODE_FROM_MASTER_LOADING, payload: true });
    const apiUrl = `/parquet/pincode-master/search?pincode=${pincode}`;
    const { data } = yield API.get(apiUrl);
    if (data.success) {
      yield put({ type: actionTypes.GET_EXT_PINCODE_FROM_MASTER_SUCCESS, payload: data.data.matches });
    } else {
      console.error('API returned success:false', data);
      toast.error("Could not load pincode data.");
      yield put({ type: actionTypes.GET_EXT_PINCODE_FROM_MASTER_FAILURE, payload: data });
    }
  } catch (error) {
      console.error('Error in getExtPincodeFromMasterSaga:', error);
      handleApiError(error, "Oops! Something went unexpectedly wrong.");
      const data = error?.response?.data;
      yield put({ type: actionTypes.GET_EXT_PINCODE_FROM_MASTER_FAILURE, payload: data || { message: "Internal server error" } });
  } finally {
      yield put({ type: actionTypes.GET_EXT_PINCODE_FROM_MASTER_LOADING, payload: false });
  }
};

function* getExtProjectsForMasterSaga({ masterProject }) {
  try {
    yield put({ type: actionTypes.GET_EXT_PROJECTS_FOR_MASTER_LOADING, payload: true });

    // masterProjects is an array of strings; the value itself is the name
    const apiUrl = `/parquet/projects-for-master?masterProject=${encodeURIComponent(masterProject)}`;
    const { data } = yield API.get(apiUrl);

    if (data.success && Array.isArray(data.data?.projects)) {
      // Use array of strings directly for type='list'
      yield put({ type: actionTypes.GET_PROJECTS_FOR_MASTER_SUCCESS, payload: data.data.projects });
    } else {
      toast.error('Could not load project list.');
      yield put({ type: actionTypes.GET_PROJECTS_FOR_MASTER_FAILURE, payload: data });
    }
  } catch (error) {
    handleApiError(error, 'Oops! Something went unexpectedly wrong.');
    const data = error?.response?.data;
    yield put({ type: actionTypes.GET_PROJECTS_FOR_MASTER_FAILURE, payload: data || { message: 'Internal server error' } });
  } finally {
    yield put({ type: actionTypes.GET_EXT_PROJECTS_FOR_MASTER_LOADING, payload: false });
  }
};

function* loadCustomColumnOptionsSaga() {
  try {
      yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
      const { data } = yield API.get("/extractions/custom-column-options");
      if (data.success) {
          yield put({ type: actionTypes.LOAD_CUSTOM_COLUMN_OPTIONS_SUCCESS, payload: data.data });
      } else {
          toast.error("Could not load page data.");
          yield put({ type: actionTypes.LOAD_CUSTOM_COLUMN_OPTIONS_FAILURE, payload: data });
      }
  } catch (error) {
      handleApiError(error, "Oops! Something went unexpectedly wrong.");
      const data = error?.response?.data;
      yield put({ type: actionTypes.LOAD_CUSTOM_COLUMN_OPTIONS_FAILURE, payload: data || { message: "Internal server error" } });
  }
};

function* getExtractionsSaga({ params }) {
    const { currentPage, perPage } = params;
    try {
        // Clear the UDS cookie when user drifts away to Extraction History
        deleteCookie("_uds_");
        yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
        const filters = yield select((state) => state.Extractions.filters);
        let queryString = `currentPage=${currentPage}&perPage=${perPage}`;
        if(filters.keyword) queryString = queryString.concat(`&keyword=${encodeURIComponent(filters.keyword)}`);
        const { data } = yield API.get(`/extractions?${queryString}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXTRACTIONS_SUCCESS, payload: data.data });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_EXTRACTIONS_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXTRACTIONS_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* createExtractionSaga({ payload }) {
  const { extractionData, navigate } = payload;
  try {
    yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
    let requestBody = {};
    if (extractionData.addTemporaryValues) {
      requestBody = {
        tempProject: extractionData.tempProject,
        tempMasterProject: extractionData.tempMasterProject,
        approxStartingPrice: extractionData.approxStartingPrice,
        pincode: extractionData.pincode
      };
    } else {
      requestBody = {
        project: extractionData.project,
        masterProject: extractionData.masterProject,
        approxStartingPrice: extractionData.approxStartingPrice,
        pincode: extractionData.pincode
      };
    }
    const { data } = yield API.post("/extractions/create", requestBody);
    if (data.success) {
      yield put({ type: actionTypes.CREATE_EXTRACTION_SUCCESS, payload: data.data });
      navigate(`/extraction/${data.data.key}`);
    } else {
      toast.error(data.message || "Could not create extraction.");
      yield put({ type: actionTypes.CREATE_EXTRACTION_FAILURE, payload: data });
    }
  } catch (error) {
      handleApiError(error, "Failed to create extraction.");
      const data = error?.response?.data;
      yield put({ type: actionTypes.CREATE_EXTRACTION_FAILURE, payload: data || { message: "Internal server error" } });
  }
};

function* getColumnListValuesSaga() {
    try {
        yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
        const { data } = yield API.get(`/parquet/column-list`);
        if (data.success) {
            yield put({ type: actionTypes.GET_COLUMN_LIST_VALUES_SUCCESS, payload: data.data.rows });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_COLUMN_LIST_VALUES_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXTRACTIONS_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getFieldValueOptionsSaga(action) {
    try {
        yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
        const { field, keyword, searchColumn, limit, pattern } = action.payload || {};
        const params = {
            field,
            ...(keyword && { keyword }),
            ...(searchColumn && { searchColumn }),
            ...(limit && { limit }),
            ...(pattern && { pattern })
        };
        const { data } = yield API.get(`/parquet/field-value-options`, { params });
        if (data.success) {
            yield put({ 
                type: actionTypes.GET_FIELD_VALUE_OPTIONS_SUCCESS, 
                payload: { 
                    field: field,
                    values: data.data.values || data.data
                }
            });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ 
                type: actionTypes.GET_FIELD_VALUE_OPTIONS_FAILURE, 
                payload: { field, data, message: data.message || "Failed to fetch options" },
              });
        }
    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const errorData = error?.response?.data;
        yield put({ 
            type: actionTypes.GET_FIELD_VALUE_OPTIONS_FAILURE, 
            payload: { 
                field: action.payload?.field,
                data: errorData,
                message: errorData?.message || "Internal server error" 
            }
        });
    }
};

function* updateExtractionSaga({ payload }) {
  const { id, formData, fieldName } = payload;
  try {
    yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
    const { data } = yield API.put(`/extractions/${id}`, formData);
    if (data.success) {
      yield put({ type: actionTypes.UPDATE_EXTRACTION_SUCCESS, payload: data.data });
      if(fieldName === "project details"){
        const ExtractionsList = yield select((state) => state.Extractions.ExtractionsList);
        yield put({
          type: actionTypes.GET_EXTRACTIONS,
          params: {
            currentPage: ExtractionsList?.pagination?.currentPage || 1,
            perPage: ExtractionsList?.pagination?.perPage || constants.tableUI.defaultPerpage,
          }
        });
      }
    } else {
      toast.error("Could not update extraction.");
      yield put({ 
        type: actionTypes.UPDATE_EXTRACTION_FAILURE, 
        payload: {
          data: { [fieldName]: data[fieldName] || data?.data?.error || data?.data?.message || "Failed to update extraction." },
          message: "Failed to update extraction."
        }
      });
    }
  } catch (error) {
    handleApiError(error, "Failed to update extraction.");
    const errorData = error?.response?.data;
    yield put({ 
      type: actionTypes.UPDATE_EXTRACTION_FAILURE, 
      payload: {
        data: { [fieldName]: errorData?.[fieldName] || "Failed to update extraction." },
        message: "Failed to update extraction."
      }
    });
  }
};

function* loadExtractionSaga({ payload }) {
  const { key, project_list=[], navigate=null, path="", setStepVal, viewMode=false } = payload;
  try {
    yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
    const { data } = yield API.get(`/extractions/${key}`);
    if (data.success) {
      const respData = data.data;
      const isCreator = respData?.isCreator;
      const selectedVersion = respData.selected_version; // Save before deleting so extVersion.id is preserved
      delete respData.selected_version;

      let finalStepVal = setStepVal;
      if (!finalStepVal) {
        const udsCookie = getCookie("_uds_");
        if (udsCookie) {
          finalStepVal = 2;
        }
      }

      yield put({ type: actionTypes.LOAD_EXTRACTION_SUCCESS, payload: respData, setStepVal: finalStepVal });

      // Restore project_list into pageLoadData immediately after LOAD_EXTRACTION_SUCCESS
      // (which resets projects to []) if it was pre-fetched and passed in the payload
      if (Array.isArray(project_list) && project_list.length > 0) {
        yield put({ type: actionTypes.SET_PAGE_LOAD_PROJECTS, payload: project_list });
      }

      // Re-fetch the projects for the loaded extraction's master project so that the
      // Step 1 project dropdown has its options populated (pageLoadData.projects would
      // otherwise remain [] when navigating to an existing extraction from a fresh state).
      const masterProjectName = respData?.project_details?.master_project;
      if (masterProjectName && !respData?.project_details?.temp_master_project_name) {
        yield put({ type: actionTypes.GET_EXT_PROJECTS_FOR_MASTER, masterProject: masterProjectName });
      }

      const tempSelectionsData = {
        selected_columns: respData.temp_selected_columns || [], 
        custom_columns: respData.temp_custom_columns || []
      };
      yield put({ type: extCustomColumnsActionTypes.SET_TEMP_SELECTIONS, payload: tempSelectionsData });

      let defaultColumnSelections = yield select((state) =>
        getDefaultColumnSelections(state.Extractions?.pageLoadData)
      );

      const hasTempSelections = 
        (Array.isArray(respData.temp_selected_columns) && respData.temp_selected_columns.length > 0) ||
        (Array.isArray(respData.temp_custom_columns) && respData.temp_custom_columns.length > 0);

      let hasDefaultSelections = hasAnySelections(defaultColumnSelections);

      if (!hasTempSelections && !hasDefaultSelections) {
        try {
          const { data: pageLoadResp } = yield API.get("/extractions/load-page-data");
          if (pageLoadResp?.success) {
            defaultColumnSelections = getDefaultColumnSelections(pageLoadResp?.data);
            yield put({ type: actionTypes.LOAD_EXT_PAGE_DATA_SUCCESS, payload: pageLoadResp?.data });
            hasDefaultSelections = hasAnySelections(defaultColumnSelections);
          }
        } catch (err) {
          // Ignore
        }
      }

      if (hasTempSelections) {
        yield put({ 
          type: extVersionActionTypes.SET_EXT_VERSION, 
          payload: {
            ...(selectedVersion || {}),
            selected_columns: respData.temp_selected_columns || [],
            custom_columns: respData.temp_custom_columns || []
          }
        });
      } else if (hasDefaultSelections) {
        yield put({ type: extVersionActionTypes.SET_EXT_VERSION, payload: { ...(selectedVersion || {}), ...defaultColumnSelections } });
      } else {
        yield put({ type: extVersionActionTypes.RESET_EXT_VERSION });
        if (selectedVersion) {
          yield put({ type: extVersionActionTypes.SET_EXT_VERSION, payload: selectedVersion });
        }
      }

      if (viewMode) {
        yield put({ type: actionTypes.SET_VIEW_MODE, payload: true });
      } else if (isCreator === false) {
        yield put({ type: actionTypes.SET_VIEW_MODE, payload: true });
      } else {
        yield put({ type: actionTypes.SET_VIEW_MODE, payload: false });
      }
      if(navigate && path) navigate(path);
    } else {
      toast.error("Could not load extraction details.");
      yield put({ type: actionTypes.LOAD_EXTRACTION_FAILURE, payload: data });
    }
  } catch (error) {
    handleApiError(error, "Oops! Something went unexpectedly wrong.");
    const data = error?.response?.data;
    yield put({ type: actionTypes.LOAD_EXTRACTION_FAILURE, payload: data || { message: "Internal server error" } });
  }
};

function* moveToNextWorkflowStepSaga({ extractionId }) {
    try {
      yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
      const { data } = yield API.put(`/extractions/next-workflow-step/${extractionId}`);
      if (data.success) {
          yield put({ type: actionTypes.NEXT_WORKFLOW_STEP_SUCCESS, payload: data.data });
      } else {
          toast.error("Could not load page data.");
          yield put({ type: actionTypes.NEXT_WORKFLOW_STEP_FAILURE, payload: data });
      }
    } catch (error) {
      handleApiError(error, "Oops! Something went unexpectedly wrong.");
      const data = error?.response?.data;
      yield put({ type: actionTypes.NEXT_WORKFLOW_STEP_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* moveToStep3Saga({ extractionId }) {
    try {
      yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
      const { data } = yield API.put(`/extractions/move-to-step3/${extractionId}`);
      if (data.success) {
          yield put({ type: actionTypes.NEXT_WORKFLOW_STEP_SUCCESS, payload: data.data });
      } else {
          toast.error("Could not load page data.");
          yield put({ type: actionTypes.NEXT_WORKFLOW_STEP_FAILURE, payload: data });
      }
    } catch (error) {
      handleApiError(error, "Oops! Something went unexpectedly wrong.");
      const data = error?.response?.data;
      yield put({ type: actionTypes.NEXT_WORKFLOW_STEP_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* saveTempSelectionsSaga({ payload }) {
    try {
        const extractionData = yield select((state) => state.Extractions.extractionData);
        if (!extractionData?.id) return;
        const extVersion = yield select((state) => state.ExtVersions.extVersion);
        yield API.patch(`/ext/${extractionData.id}/temp-selections`, {
            selected_columns: extVersion.selected_columns,
            custom_columns: extVersion.custom_columns,
        });
    } catch (error) {
        console.error("Failed to save temp selections:", error);
    }
};

function* resetFormSaga() {
    try {
        const extractionData = yield select((state) => state.Extractions.extractionData);
        const udsCookie = getCookie("_uds_");
        
        if (extractionData && extractionData.id && udsCookie) {
            try {
                yield API.delete(`/ext/${extractionData.id}/versions/abandon-draft`);
            } catch (err) {
                console.error("Failed to abandon draft session:", err);
            }
        }

        yield put({ type: actionTypes.SET_VIEW_MODE, payload: false });
        yield put({ type: extDataGroupActionTypes.GET_EXT_DATA_GROUPS_SUCCESS, payload: constants.extraction.listInitialState });
        yield put({ type: extDataCategoryActionTypes.GET_EXT_DATA_CATEGORIES_SUCCESS, payload: constants.extraction.listInitialState });
        yield put({ type: extExclusionActionTypes.GET_EXT_EXCLUSIONS_SUCCESS, payload: constants.extraction.listInitialState });
        yield put({ type: extVersionActionTypes.GET_EXT_VERSIONS_SUCCESS, payload: constants.extraction.listInitialState });
        yield put({ type: extVersionActionTypes.RESET_EXT_VERSION });
        yield put({ type: extCustomColumnsActionTypes.GET_EXT_CUSTOM_COLUMNS_SUCCESS, payload: constants.extraction.listInitialState });
        yield put({ type: extCustomColumnsActionTypes.SET_TEMP_SELECTIONS, payload: EMPTY_TEMP_SELECTIONS });
        yield put({ type: actionTypes.SET_VIEW_MODE, payload: false });
        yield put({ type: actionTypes.SET_EXTRACTION_DATA, payload: constants.extraction.dataInitialState });
        yield put({ type: actionTypes.SET_EXT_ERRORS, payload: constants.extraction.errorInitialState });
        yield put({ type: actionTypes.SET_EXT_LOADING, payload: false });
        yield put({ type: actionTypes.SET_STEP_VAL, payload: 1 });
        // Clear the UDS cookie when form resets (e.g. drifting to New Extraction or other modules)
        deleteCookie("_uds_");
        deleteCookie("_uds_");
    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
    }
};

// -------------------- VIEW FULL DATA --------------------
// Both endpoints now return the same shape:
// { success: true, data: { data: [...], meta: {...}, pagination: { currentPage, perPage, total, totalPages } } }

function* viewFullDataSaga({ payload }) {
  const {
    extOutputMappingId,
    currentPage,
    perPage,
    navigate = null,
    refreshPagination = false
  } = payload;

  try {
    yield put({ type: actionTypes.START_SHOW_FULL_DATA });

    const { FullDataList, fullDataFilters } = yield select((state) => state.Extractions);
    const previousMeta = FullDataList?.meta;


    // Use extOutputMappingId as the context key
    const isSameContext = previousMeta?.extOutputMappingId === extOutputMappingId;

    const shouldIngest =
      refreshPagination ||
      !isSameContext ||
      !FullDataList?.pagination?.total;

    if (shouldIngest) {
      yield put({ type: actionTypes.SET_FULL_DATA_PAGINATION_LOADING, payload: true });
    }

    // Only extOutputMappingId is allowed for parquet-ingest endpoints
    if (!extOutputMappingId) {
      toast.error('Output mapping ID is required to view data.');
      yield put({ type: actionTypes.SET_FULL_DATA_LOADING, payload: false });
      return;
    }
    const requestBody = {
      extOutputMappingId,
      currentPage: shouldIngest ? 1 : currentPage,
      perPage,
      keyword: fullDataFilters?.keyword || ''
    };
    const endpoint = shouldIngest ? '/parquet-ingest/start' : '/parquet-ingest/read';
    const { data } = yield API.post(endpoint, requestBody);

    if (data.success) {
        const respData = data.data;

        yield put({
            type: actionTypes.VIEW_FULL_DATA_SUCCESS,
            payload: { data: respData, endpoint },
        });

        if (navigate) {
            const meta = respData?.meta;
            if (meta?.extVersion?.key && meta?.dataGroup?.title && meta?.dataCategory?.title) {
                navigate(
                    `/extraction/view-full-data/${meta.extVersion.key}/${spacesToUnderscores(meta.dataGroup.title)}/${spacesToUnderscores(meta.dataCategory.title)}`
                );
            }
        }

        // POLL FOR INGESTION PROGRESS PERCENTAGE
        yield put({ type: actionTypes.POLL_INGESTION_PROGRESS });

    } else if (data.status === 'not_ready') {
        toast.warn(data.message || 'This page is not ready yet. Please wait a moment and try again.');
        yield put({ type: actionTypes.SET_FULL_DATA_LOADING, payload: false });
        yield put({ type: actionTypes.SET_FULL_DATA_PAGINATION_LOADING, payload: false });

    } else {
        const errorMessage = data?.message || data?.error || 'Could not load full data.';
        toast.error(errorMessage);
        yield put({ type: actionTypes.VIEW_FULL_DATA_FAILURE, payload: data });
    }

  } catch (error) {
    handleApiError(error, "Failed to load full data.");
    const errorData = error?.response?.data;
    toast.error(errorData?.message || errorData?.error || "Failed to load full data.");
    yield put({
      type: actionTypes.VIEW_FULL_DATA_FAILURE,
      payload: errorData || { message: "Internal server error" }
    });

  } finally {
    yield put({ type: actionTypes.SET_FULL_DATA_LOADING, payload: false });
    yield put({ type: actionTypes.SET_FULL_DATA_PAGINATION_LOADING, payload: false });
    yield put({ type: actionTypes.SET_EXT_LOADING, payload: false });
  }
}

function* setSelectedVersionSaga({ payload }) {
  const { extractionId, versionId } = payload;
  try {
    yield put({ type: actionTypes.SET_EXT_LOADING, payload: true });
    const { data } = yield API.put(`/extractions/${extractionId}/selected-version`, { versionId });
    if (data.success) {
      toast.success("Selected version updated successfully.");
      const updatedExtraction = data.data;
      yield put({ type: actionTypes.LOAD_EXTRACTION_SUCCESS, payload: updatedExtraction });
      yield put({ type: extVersionActionTypes.SET_EXT_VERSION, payload: updatedExtraction.selected_version });
    } else {
      toast.error(data.message || "Could not update selected version.");
      yield put({ type: actionTypes.SET_SELECTED_VERSION_FAILURE, payload: data });
    }
  } catch (error) {
    handleApiError(error, "Failed to update selected version.");
    const errorData = error?.response?.data;
    yield put({ type: actionTypes.SET_SELECTED_VERSION_FAILURE, payload: errorData || { message: "Internal server error" } });
  }
};


function* pollIngestionProgressSaga() {
  try {
    // Initial delay before polling starts
    yield new Promise(resolve => setTimeout(resolve, constants.extraction.parquetIngestion.initialDelay));
    while (true) {
      const { data } = yield API.get('/parquet-ingest/progress');
      const ingestion = {
        progress: data?.percentage || 0,
        action: data?.action_in_progress || ""
      };
      yield put({ type: actionTypes.SET_INGESTION_PROGRESS, payload: ingestion });

      if (ingestion.progress === 100) break;
      yield new Promise(resolve => setTimeout(resolve, constants.extraction.parquetIngestion.pollInterval));
    }
  } catch (e) {
    // Optionally handle error, retry, or break
  } finally {
    if (yield cancelled()) {
      // Optionally handle cancellation cleanup
    }
  }
}

function* watchPollIngestionProgress() {
  let task;
  while (true) {
    yield take(actionTypes.POLL_INGESTION_PROGRESS);
    if (task) {
      yield cancel(task);
    }
    task = yield fork(pollIngestionProgressSaga);
    yield take('STOP_POLL_INGESTION_PROGRESS');
    if (task) {
      yield cancel(task);
      task = null;
    }
  }
}


function* ExtractionsSaga() {
  yield all([
    takeLatest(actionTypes.LOAD_EXT_PAGE_DATA, getExtPageDataSaga),
    takeLatest(actionTypes.GET_EXT_PROJECTS_FOR_MASTER, getExtProjectsForMasterSaga),
    takeLatest(actionTypes.GET_EXT_META_DATA, getExtMetaDataSaga),
    takeLatest(actionTypes.GET_EXT_PINCODE_FROM_MASTER, getExtPincodeFromMasterSaga),
    takeLatest(actionTypes.GET_EXT_PINCODE_FROM_MASTER, getExtPincodeFromMasterSaga),
    takeLatest(actionTypes.GET_EXT_PINCODE_FROM_MASTER, getExtPincodeFromMasterSaga),
    takeLatest(actionTypes.CREATE_EXTRACTION, createExtractionSaga),
    takeLatest(actionTypes.UPDATE_EXTRACTION, updateExtractionSaga),
    takeLatest(actionTypes.GET_EXTRACTIONS, getExtractionsSaga),
    takeLatest(actionTypes.LOAD_EXTRACTION, loadExtractionSaga),
    takeEvery(actionTypes.GET_FIELD_VALUE_OPTIONS, getFieldValueOptionsSaga),
    takeLatest(actionTypes.GET_COLUMN_LIST_VALUES, getColumnListValuesSaga),
    takeLatest(actionTypes.NEXT_WORKFLOW_STEP, moveToNextWorkflowStepSaga),
    takeLatest(actionTypes.MOVE_TO_STEP3, moveToStep3Saga),
    takeLatest(actionTypes.LOAD_CUSTOM_COLUMN_OPTIONS, loadCustomColumnOptionsSaga),
    takeLatest(actionTypes.SAVE_TEMP_SELECTIONS, saveTempSelectionsSaga),
    takeLatest(actionTypes.VIEW_FULL_DATA, viewFullDataSaga),
    takeLatest(actionTypes.RESET_FORM, resetFormSaga),
    takeLatest(actionTypes.SET_SELECTED_VERSION, setSelectedVersionSaga),
    fork(watchPollIngestionProgress)
  ]);
}

export default ExtractionsSaga;